/**
 * Global Shortcuts Service
 * 
 * Manages system-wide keyboard shortcuts that work anywhere in RiverOS.
 * Similar to Cmd+K for AI, but for any global action.
 * 
 * Architecture:
 * - Registers at App.tsx level (below WindowWrapper level)
 * - Single event listener for all global shortcuts
 * - Apps can register their global shortcuts here
 * - Handles conflicts and priority
 * - Works even when app isn't open
 *
 * Conflicts: several shortcuts may claim the same combo. They are kept in a
 * priority-ordered chain (highest first) and tried in turn; a handler that
 * returns `false` DECLINES and the next one gets a shot. This is what lets
 * Cmd+K mean "contextual AI on the current selection" when there is a
 * selection and "global search" when there isn't, without either registration
 * having to know about the other.
 */

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('GlobalShortcuts', { enabled: false });

export interface GlobalShortcut {
  id: string;
  key: string;
  modifiers: {
    cmd?: boolean;
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
  };
  description: string;
  /**
   * Return `false` to decline the keypress and let the next-highest-priority
   * shortcut on the same combo handle it. Anything else (including undefined)
   * counts as handled and stops the chain.
   */
  handler: () => boolean | void | Promise<void>;
  priority?: number; // Higher priority = tried first (for conflicts)
  enabled?: boolean;
}

class GlobalShortcutsService {
  private shortcuts: Map<string, GlobalShortcut[]> = new Map();
  private isListening = false;

  /**
   * Register a global shortcut
   */
  register(shortcut: GlobalShortcut) {
    const key = this.getShortcutKey(shortcut.key, shortcut.modifiers);
    
    log.log('🎹 [GlobalShortcuts] Registering shortcut:', {
      id: shortcut.id,
      key: key,
      description: shortcut.description,
      modifiers: shortcut.modifiers
    });

    // Re-registering the same id replaces its entry rather than stacking a
    // duplicate (hooks re-register whenever their handler identity changes).
    const chain = (this.shortcuts.get(key) ?? []).filter((s) => s.id !== shortcut.id);
    chain.push(shortcut);
    chain.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    this.shortcuts.set(key, chain);

    log.log('✅ [GlobalShortcuts] Registered global shortcut:', key, {
      id: shortcut.id,
      chain: chain.map((s) => s.id),
      totalCombos: this.shortcuts.size
    });

    // Start listening if not already
    if (!this.isListening) {
      log.log('🎧 [GlobalShortcuts] Starting listener...');
      this.startListening();
    }
  }

  /**
   * Unregister a global shortcut
   */
  unregister(shortcutId: string) {
    log.log('🗑️ [GlobalShortcuts] Unregistering shortcut:', shortcutId);
    
    for (const [key, chain] of this.shortcuts.entries()) {
      const next = chain.filter((s) => s.id !== shortcutId);
      if (next.length === chain.length) continue;
      if (next.length === 0) {
        this.shortcuts.delete(key);
      } else {
        this.shortcuts.set(key, next);
      }
      log.log('✅ [GlobalShortcuts] Unregistered:', key, {
        id: shortcutId,
        remainingCombos: this.shortcuts.size
      });
      break;
    }

    // Stop listening if no shortcuts left
    if (this.shortcuts.size === 0) {
      log.log('🛑 [GlobalShortcuts] No shortcuts left, stopping listener');
      this.stopListening();
    }
  }

  /**
   * Enable/disable a shortcut without unregistering
   */
  setEnabled(shortcutId: string, enabled: boolean) {
    for (const [key, chain] of this.shortcuts.entries()) {
      const shortcut = chain.find((s) => s.id === shortcutId);
      if (shortcut) {
        shortcut.enabled = enabled;
        log.info(`${enabled ? 'Enabled' : 'Disabled'} global shortcut: ${key}`);
        break;
      }
    }
  }

  /**
   * Get all registered shortcuts (for UI display)
   */
  getAllShortcuts(): GlobalShortcut[] {
    return Array.from(this.shortcuts.values()).flat();
  }

  /**
   * Generate shortcut key for map
   */
  private getShortcutKey(key: string, modifiers: GlobalShortcut['modifiers']): string {
    if (!key) {
      console.warn('⚠️ [GlobalShortcuts] Invalid key provided to getShortcutKey');
      return '';
    }
    
    const parts: string[] = [];
    if (modifiers.cmd) parts.push('cmd');
    if (modifiers.ctrl) parts.push('ctrl');
    if (modifiers.shift) parts.push('shift');
    if (modifiers.alt) parts.push('alt');
    parts.push(key.toLowerCase());
    return parts.join('+');
  }

  /**
   * Check if user is typing in an input field
   */
  private isTypingInInput(target: HTMLElement): boolean {
    return (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.closest('[contenteditable="true"]') !== null
    );
  }

  /**
   * Handle keyboard event
   */
  private handleKeyDown = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;

    // Guard against undefined keys (can happen with IME composition events)
    if (!e.key) {
      return;
    }

    // Build current key combination
    // Normalize modifiers: treat Ctrl as Cmd for cross-platform compatibility
    // On Mac: metaKey = Cmd, ctrlKey = Ctrl
    // On Windows/Linux: metaKey = Windows key, ctrlKey = Ctrl (which we treat as Cmd)
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const cmdPressed = e.metaKey || (!isMac && e.ctrlKey);
    
    const modifiers: GlobalShortcut['modifiers'] = {
      cmd: cmdPressed,
      ctrl: isMac && e.ctrlKey, // Only treat Ctrl separately on Mac
      shift: e.shiftKey,
      alt: e.altKey
    };

    const key = this.getShortcutKey(e.key, modifiers);
    
    const chain = this.shortcuts.get(key);

    if (!chain || chain.length === 0) {
      // Shortcut not registered, ignore silently
      return;
    }

    // Global shortcuts should work EVERYWHERE (even in inputs)
    // This is intentional - they're truly global
    // If you want input-aware behavior, use app-level shortcuts instead

    // Walk the priority chain until a handler claims the keypress. Handlers run
    // before preventDefault so a fully declined combo still reaches the browser
    // and any app-level handler underneath it.
    for (const shortcut of chain) {
      if (shortcut.enabled === false) {
        log.log('⏸️ [GlobalShortcuts] Shortcut disabled:', key, shortcut.id);
        continue;
      }

      log.log('🎯 [GlobalShortcuts] Shortcut matched! Executing:', {
        key: key,
        id: shortcut.id,
        description: shortcut.description
      });

      let declined = false;
      try {
        declined = shortcut.handler() === false;
      } catch (error) {
        console.error('❌ [GlobalShortcuts] Error executing handler:', error);
        log.error(`Error executing global shortcut ${key}:`, error);
      }

      if (declined) {
        log.log('↪️ [GlobalShortcuts] Handler declined, trying next:', shortcut.id);
        continue;
      }

      e.preventDefault();
      e.stopPropagation();
      log.log('✅ [GlobalShortcuts] Handler executed successfully');
      return;
    }

    log.log('🙈 [GlobalShortcuts] Every handler declined, passing through:', key);
  };

  /**
   * Start listening for keyboard events
   */
  private startListening() {
    if (this.isListening) {
      log.log('⚠️ [GlobalShortcuts] Already listening, skipping');
      return;
    }

    log.log('🎧 [GlobalShortcuts] Adding keydown event listener (capture phase)');
    document.addEventListener('keydown', this.handleKeyDown, { capture: true });
    this.isListening = true;
    log.log('✅ [GlobalShortcuts] Now listening for shortcuts:', {
      isListening: this.isListening,
      comboCount: this.shortcuts.size,
      combos: Array.from(this.shortcuts.keys())
    });
  }

  /**
   * Stop listening for keyboard events
   */
  private stopListening() {
    if (!this.isListening) {
      log.log('⚠️ [GlobalShortcuts] Not listening, skipping stop');
      return;
    }

    log.log('🛑 [GlobalShortcuts] Removing keydown event listener');
    document.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    this.isListening = false;
    log.log('✅ [GlobalShortcuts] Stopped listening');
  }

  /**
   * Cleanup (call on app unmount)
   */
  cleanup() {
    this.stopListening();
    this.shortcuts.clear();
    log.info('Global shortcuts service cleaned up');
  }
}

// Singleton instance
export const globalShortcutsService = new GlobalShortcutsService();

/**
 * React hook for registering global shortcuts
 * Auto-cleanup on unmount
 */
export const useGlobalShortcut = (shortcut: GlobalShortcut) => {
  React.useEffect(() => {
    globalShortcutsService.register(shortcut);
    
    return () => {
      globalShortcutsService.unregister(shortcut.id);
    };
  }, [shortcut.id]); // Only re-register if ID changes
};

// Export for direct import
import React from 'react';

